import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["messages", "input", "submitBtn", "typing"];
  static values = { url: String, botName: String, youName: String };

  connect() {
    this._messages = [];
    this._boundOpen = this._open.bind(this);
    document.addEventListener("promote:open-ai-assistant-preview", this._boundOpen);
  }

  disconnect() {
    document.removeEventListener("promote:open-ai-assistant-preview", this._boundOpen);
  }

  async send(event) {
    event.preventDefault();

    const text = this.inputTarget.value.trim();
    if (!text || this.submitBtnTarget.disabled) return;

    this._messages.push({ role: "user", content: text });
    this._appendMessage("user", text);
    this.inputTarget.value = "";
    this._setLoading(true);
    this._showTyping();

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ messages: this._messages })
      });

      const data = await response.json();
      this._hideTyping();

      if (!response.ok) {
        this._appendError(data.error);
        this._messages.pop();
        return;
      }

      this._messages.push({ role: "assistant", content: data.text });
      this._appendMessage("assistant", data.html);
    } catch {
      this._hideTyping();
      this._appendError();
      this._messages.pop();
    } finally {
      this._setLoading(false);
      this.inputTarget.focus();
    }
  }

  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.element.querySelector("form").dispatchEvent(new Event("submit", { bubbles: true }));
    }
  }

  _open() {
    this._messages = [];
    const onClose = this.panelEditorController.closePanel.bind(this.panelEditorController);
    this.panelEditorController.openPanel(onClose);
  }

  _appendMessage(role, content) {
    const isUser = role === "user";
    const { wrapper, contentInner } = this._buildBubbleShell(isUser);

    if (isUser) {
      contentInner.textContent = content;
    } else {
      contentInner.innerHTML = content;
    }

    this.messagesTarget.appendChild(wrapper);
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
  }

  _buildBubbleShell(isUser) {
    const wrapper = document.createElement("div");
    wrapper.className = `flex flex-col gap-1.5 ${isUser ? "items-end" : "items-start"}`;

    const header = document.createElement("div");
    header.className = "flex items-center gap-1.5";

    const avatarTemplate = document.getElementById(
      isUser ? "ai-assistant-preview-user-avatar" : "ai-assistant-preview-bot-avatar"
    );
    if (avatarTemplate) {
      header.appendChild(avatarTemplate.content.cloneNode(true));
    }

    const nameEl = document.createElement("span");
    nameEl.className = "text-base text-slate-800";
    nameEl.textContent = isUser
      ? (this.youNameValue || "You")
      : (this.botNameValue || "AI Assistant");
    header.appendChild(nameEl);

    const bubble = document.createElement("div");
    bubble.className = isUser
      ? "w-full max-w-[400px] p-5 rounded-md bg-white border border-slate-600 text-slate-800"
      : "w-full max-w-[400px] p-5 rounded-md bg-purple-700 text-white";

    const contentInner = document.createElement("div");
    contentInner.className =
      "flex flex-col promote-space-y-3 original-content text-base break-words";
    bubble.appendChild(contentInner);

    wrapper.appendChild(header);
    wrapper.appendChild(bubble);

    return { wrapper, contentInner };
  }

  _showTyping() {
    this.typingTarget.classList.remove("hidden");
    this.messagesTarget.appendChild(this.typingTarget);
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
  }

  _hideTyping() {
    this.typingTarget.classList.add("hidden");
  }

  _appendError(message) {
    const wrapper = document.createElement("div");
    wrapper.className = "flex w-full justify-center";
    const bubble = document.createElement("div");
    bubble.className = "p-3 bg-red-50 text-red-700 text-sm border border-red-200 rounded";
    bubble.textContent = message || "Something went wrong. Please try again.";
    wrapper.appendChild(bubble);
    this.messagesTarget.appendChild(wrapper);
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
  }

  _setLoading(loading) {
    this.submitBtnTarget.disabled = loading;
    this.inputTarget.disabled = loading;
  }

  get panelEditorController() {
    return this.application.getControllerForElementAndIdentifier(this.element, "panel-editor");
  }
}
